"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = Pivot;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _chevronRight = _interopRequireDefault(require("@ux/icon/chevron-right"));
var _linkArrow = _interopRequireDefault(require("@ux/icon/link-arrow"));
var _classes = _interopRequireDefault(require("@ux/text/classes"));
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
/* eslint-disable no-undefined, complexity */

/**
 * Creates image or icon accessory
 *
 * @param {Object} props - Configuration object
 * @param {Node} props.children - Element to render within
 * @param {String} props.className - Class name to use for styles
 * @returns {Node} - <Accessory/> element
 */
function Accessory(_ref) {
  let {
    children,
    className
  } = _ref;
  return /*#__PURE__*/_react.default.createElement("span", {
    className: (0, _classnames.default)('ux-pivot-accessory', className),
    "aria-hidden": "true"
  }, children);
}
Accessory.propTypes = {
  /** Element to render within */
  children: _propTypes.default.node,
  /** Class name to use for styles */
  className: _propTypes.default.string
};

/**
 * Creates an individual pivot
 *
 * @param {Object} props - Component props
 * @param {Boolean} [props.arrow] - Determines if the graphic should be an arrow
 * @param {Boolean} [props.disabled] - Disables the individual pivot
 * @param {Node} [props.graphic] - Visual accessory
 * @param {String} [props.href] - URL for <a/> usage
 * @param {String} props.id - Unique identifier for a11y
 * @param {Function} [props.onClick] - Click handler function
 * @param {Function} [props.onSelected] - Fires when the element should be shown as selected
 * @param {Boolean} [props.selected] - Represents the selected state
 * @param {Node} [props.title] - Title to be shown
 * @param {Node} [props.subtitle] - Content to be shown below title
 * @returns {Element} - Pivot component
 */
function Pivot(props) {
  const {
    arrow = false,
    disabled = false,
    graphic,
    href,
    id,
    onClick,
    onSelected,
    selected = false,
    title,
    subtitle
  } = props;
  const Tag = !href && typeof onClick === 'function' ? 'button' : 'a';
  const ariaLabelledBy = id ? `title-${id}` : null;
  const handleClick = (0, _react.useCallback)(ev => {
    if (disabled) return ev.preventDefault();
    typeof onClick === 'function' && onClick(ev);
    !href && typeof onSelected === 'function' && onSelected();
  }, [disabled, onClick, href, onSelected]);
  return /*#__PURE__*/_react.default.createElement(Tag, {
    className: (0, _classnames.default)('ux-pivot-child', {
      'ux-pivot-center': !arrow
    }),
    href: href,
    onClick: handleClick,
    "aria-labelledby": ariaLabelledBy,
    "aria-selected": selected,
    "aria-disabled": disabled ? true : null,
    id: id ? id : null
  }, graphic && /*#__PURE__*/_react.default.createElement(Accessory, {
    className: "ux-pivot-img"
  }, graphic), /*#__PURE__*/_react.default.createElement("div", null, /*#__PURE__*/_react.default.createElement("div", {
    id: ariaLabelledBy,
    className: (0, _classes.default)({
      as: 'title'
    })
  }, title), subtitle), /*#__PURE__*/_react.default.createElement(Accessory, {
    className: "ux-pivot-chevron"
  }, /*#__PURE__*/_react.default.createElement(_chevronRight.default, null)), !graphic && arrow && /*#__PURE__*/_react.default.createElement(Accessory, {
    className: "ux-pivot-arrow"
  }, /*#__PURE__*/_react.default.createElement(_linkArrow.default, null)));
}

/**
 * Define Properties
 * @public
 */
Pivot.propTypes = {
  /**
   * link to be assigned to this pivot
   * @function hrefValidate
   * @param {Object} props React props
   * @param {String} propName Name of this href prop
   * @returns {Error|null} validation
   * */
  href: function hrefValidate(props, propName) {
    const value = props[propName];
    if (value && typeof value !== 'string') {
      return new Error('href must be a string');
    }
    if (!value && !props.onClick) {
      return new Error('Either href or onClick required for Pivot');
    }
    return null;
  },
  /**
   * Validate label and require ID if label is a ReactElement
   *
   * @param {Object} props the props passed in
   * @param {string} propName the name of the current prop
   * @param {string} componentName the name of the component
   * @returns {Error|null} validation
   */
  graphic: function validatePropTypeGraphic(props, propName, componentName) {
    if (!props.arrow) {
      _propTypes.default.checkPropTypes({
        graphic: _propTypes.default.element.isRequired
      }, props, propName, componentName);
    }
    return null;
  },
  /** title for the tile/slat */
  title: _propTypes.default.node.isRequired,
  /** optional subtitle for tile/slat */
  subtitle: _propTypes.default.node,
  /** the specified design, `slats` is special cased */
  design: _propTypes.default.string,
  /** click handler */
  onClick: _propTypes.default.func,
  /** onSelected handler */
  onSelected: _propTypes.default.func,
  /** whether this tile/slat is currently selected */
  selected: _propTypes.default.bool,
  /** whether this tile/slat is rendered in disabled state */
  disabled: _propTypes.default.bool,
  /** An optional unique id attribute for the element */
  id: _propTypes.default.string,
  /** adds a specific class to change the way a pivot looks */
  arrow: _propTypes.default.bool
};